import type { PermissionRequirement } from '../permissions/resolve.ts';
/**
 * Spaltenvertrag des Listen-Baukastens (AD-32, FR-91): Jede Liste hat einen festen Schlüssel und genau einen
 * Hauptbesitzer; ihre Spalten sind die Summe der Spaltenbeiträge der Module. Rein (ohne Datenbank): Server,
 * `reporting` und Web-App nutzen dieselben Typen.
 */
import type { ListColumnRight } from '../permissions/list-rights.ts';
import type { ListEditMode } from './edit.ts';

/** Spaltentypen (AD-32). Der Typ bestimmt die Filter-Operatoren und die Darstellung in der Zelle. */
export const listColumnTypes = [
  'text',
  'number',
  'money',
  'quantity',
  'percent',
  'date',
  'datetime',
  'boolean',
  'enum',
  'multi_enum',
  'status',
  'image',
  // Story 7.23: Reihe kleiner Signale (z. B. Ampel je Prüfung); nur anzeigbar, Regel in `signals.ts`.
  'signals',
] as const;
export type ListColumnType = (typeof listColumnTypes)[number];

/**
 * Farbe einer Status-Pille (DESIGN „Status-Werte und Farbe“): Bereit grün, Prüfen bernstein, Klärfall rot (je mit Punkt),
 * Wartet grau mit Uhr, neutral grau nur mit Wort.
 */
export const listStatusTones = ['ready', 'check', 'clarify', 'waiting', 'neutral'] as const;
export type ListStatusTone = (typeof listStatusTones)[number];

/** Auswahlwert mit deutscher Beschriftung (nie den technischen Wert zeigen). */
export interface ListColumnOption {
  value: string;
  label: string;
  /** Nur Typ `status`: Farbe der Pille; ohne Angabe grau nur mit Wort. */
  tone?: ListStatusTone;
}

/**
 * Operationen der Massenbearbeitung (AD-32, Story 1.21; EXPERIENCE §4.9): Text setzen · leeren · ersetzen ·
 * voranstellen · anhängen; Zahl/Preis setzen · + / − Prozent · + / − Betrag · runden; Auswahl setzen · leeren;
 * Mehrfachwerte hinzufügen · entfernen · ersetzen durch (Story 5.89 zusätzlich setzen · leeren · ersetzen A → B); Ja/Nein
 * setzen. Rechnen: `edit.ts`.
 */
export const listEditOperations = [
  'set',
  'clear',
  'add',
  'remove',
  'replace',
  'increase_percent',
  'increase_amount',
  'round',
  'text_replace',
  'prepend',
  'append',
  /** Betrag aus dem EK des Hauptlieferanten × Faktor mit Preisendung (Story 5.59, `purchase-price-rule.ts`). */
  'from_purchase_price',
  /** Mehrfachwerte: einen Wert A durch B ersetzen („ersetzen TiAlN → AlCrN“, Story 5.89); fehlt A, bleibt alles. */
  'swap',
] as const;
export type ListEditOperation = (typeof listEditOperations)[number];

/**
 * Bearbeitbarkeit einer Spalte in der Massenbearbeitung (Story 1.21): Besitzer, Datensatzart und Feld seines
 * Änderungspaket-Handlers (AD-3) und die erlaubten Operationen. Nur Felder, die man auch einzeln ändern darf.
 */
export interface ListColumnEdit {
  /** Besitzer-Modul des Handlers (AD-2), z. B. `customers`. */
  owner: string;
  /** Datensatzart des Handlers, z. B. `customer`. */
  entityType: string;
  /** Feld des Handlers, z. B. `language`. */
  field: string;
  operations: readonly ListEditOperation[];
  /**
   * Mindestrolle für dieses Feld in der Massenbearbeitung (Story 1.29, AD-32): `admin` → nur Admins sehen es in der
   * Feld-Leiste, sonst 403; fehlt die Angabe, dürfen es alle (der Command des Besitzers prüft beim Ausführen zusätzlich).
   */
  role?: 'user' | 'admin';
  /**
   * Nur Typ `money`: Artikel-Verkaufspreis (Story 2.18, FR-97). Nur solche Betragsfelder darf die KI per Satz
   * vorschlagen; alle anderen Beträge (z. B. Kreditlimit) bleiben per Satz gesperrt (Rudolf 11.10.2026).
   */
  salesPrice?: true;
  /**
   * Erlaubte Modi der Feldzeile (Story 5.12), Teilmenge von `editModesFor`; ohne Angabe alle passenden. Z. B. Spalten mit
   * wirksamen (geerbten) Werten: kein `formula`/`copy_from`, damit ein geerbter Wert nie als eigener Wert geschrieben wird.
   */
  modes?: readonly ListEditMode[];
  /**
   * Nur Typ `money`: Nachkommastellen in der Massenbearbeitung (Story 5.60). `4` → die Werte dieser Spalte sind in der
   * Massenbearbeitung Euro-Dezimaltext mit genau 4 Nachkommastellen (`"12.3456"`) statt Cent; gerechnet wird auf 10⁻⁴
   * (half-up). Eine reine Ziffernfolge bedeutet dann Cent (Altbestand vor 5.60). Liste, Filter, Export bleiben in Cent.
   */
  scale?: 4;
}

/** Zeilen der Karte am Handy (Story 1.23). */
export const mobilePriorities = [1, 2, 3] as const;
export type MobilePriority = (typeof mobilePriorities)[number];

/** Eine Spalte einer Liste. */
export interface ListColumn {
  /** Fester Schlüssel `<modul>.<feld>`, z. B. die Kundennummer aus `customers`. */
  key: string;
  /** Modul, das die Spalte beiträgt (Besitzer der Report-View `report.list_<liste>__<modul>`). */
  module: string;
  /** Spalte der Report-View. Bezeichner nur aus dem Code, nie aus dem Browser. */
  field: string;
  /**
   * Teil-View des Moduls (Bug 5.49): Die Spalte kommt aus `report.list_<liste>__<modul>__<view>` statt aus der
   * Modul-View. Für Daten aus einer zweiten Tabelle mit eigenem Schlüssel (z. B. Lagerplatz neben dem Bestand), damit
   * jede View über ihren Primärschlüssel verbunden wird (Index je Zeile statt FULL JOIN). Nicht beim Hauptbesitzer.
   */
  view?: string;
  /**
   * Sortier-Teil-View des Hauptbesitzers (Story 5.52): Die Spalte zeigt und filtert den Wert der Hauptview, sortiert
   * aber über `report.list_<liste>__<modul>__<sortView>` – eine View mit demselben Feld direkt über der Tabelle mit
   * Index `(feld, schlüssel)` (z. B. Shop-Spalten hinter einem `LEFT JOIN` der Hauptview). Gleiche Werte je Schlüssel
   * vorausgesetzt; nur beim Hauptbesitzer, nur sortierbar.
   */
  sortView?: string;
  /**
   * Sortierschlüssel (Story 5.57): Die Spalte zeigt und filtert `field`, sortiert aber nach diesem Feld derselben View
   * (z. B. die ersten 500 Zeichen eines langen Texts, damit der Index-Eintrag nie überläuft). Bei gleichem Sortierschlüssel
   * entscheidet der volle Wert (`field`), erst dann der Schlüssel – die Kürzung ändert die Reihenfolge nie. Nur für
   * sortierbare Spalten.
   */
  sortField?: string;
  /**
   * Gleich-Filter über einen Hash-Index (Story 5.57): „ist gleich“ (Text) und „ist eine von“ (Auswahl) grenzen zuerst über
   * den Index `(…, md5(<Such-Faltung>(feld)))` des Besitzers ein, dann gilt der volle Vergleich. Für Texte, die für einen
   * B-Baum-Eintrag zu lang sein können. Nur für filterbare Text- und Auswahlspalten.
   */
  hashedEquality?: boolean;
  /**
   * Recht, das die Rolle für diese Spalte braucht (Story 11.41, `canViewCustomers`/`canViewOrders` in
   * `permissions/list-rights.ts`). Fehlt es, fällt die Spalte für diese Rolle aus dem Spaltenkatalog, den Zeilen, Filtern,
   * der Schnellsuche, Sortierung, Gruppen, Kennzahlen, dem Export, den gespeicherten Ansichten und der KI heraus – im
   * Server, nicht nur in der Oberfläche. Nie ein Rollenname.
   */
  requires?: ListColumnRight;
  /** Erforderliches Leserecht; gleicher Schutz für alle Abfragen und Exporte (AD-43). */
  readPermission?: PermissionRequirement;
  /** Deutsche Beschriftung. */
  label: string;
  /** Kurze Beschriftung nur für den Spaltenkopf der Tabelle (z. B. „Umsatz 12 M.“); `label` steht dann als Tooltip und für Screenreader. */
  headerLabel?: string;
  /**
   * Beschriftung bei Namensgleichheit (Story 5.91, Spaltenfamilien): Heißt eine Spalte derselben Liste ohne diese Angabe
   * genauso (ohne Groß/klein, getrimmt), setzt `reporting` beim Bau des Registers diese Beschriftung statt `label` ein –
   * z. B. kurz „Durchmesser“, bei Gleichheit mit einer festen Spalte „Eigenschaft: Hersteller“.
   */
  labelOnCollision?: string;
  /** Gruppe in der Spaltenwahl, z. B. „Stammdaten“. */
  group: string;
  type: ListColumnType;
  /** Nur mit passendem Index des Besitzers (AD-32 „Leistung“). */
  filterable: boolean;
  /** Nur mit passendem Index des Besitzers (AD-32 „Leistung“). */
  sortable: boolean;
  /** Teil der Startspalten (Reihenfolge = Reihenfolge im Beitrag). */
  defaultVisible: boolean;
  /** Startbreite in px. */
  width?: number;
  /** Beschriftungen der Werte (Auswahl, Status, Mehrfachwerte). */
  options?: readonly ListColumnOption[];
  /** Die Auswahl im Filter bietet zusätzlich die vorhandenen Werte der Liste an (z. B. Länder). */
  distinctOptions?: boolean;
  /** Schlüssel einer Spalte, deren Wert grau unter dem Wert dieser Spalte steht (z. B. Name unter Kundennummer). */
  subtitle?: string;
  /** Den Wert von `subtitle` auch zeigen, wenn diese Spalte leer ist („– · Gewicht fehlt“, Story 5.84). */
  subtitleWhenEmpty?: boolean;
  /**
   * Kurzwort vor dem Wert (Story 7.8, DESIGN „Tabelle“: „Express“ vor der Auftragsnummer): Schlüssel einer Ja/Nein-Spalte
   * derselben Liste und das Wort. Ist ihr Wert wahr, steht `label` in derselben Schrift vor dem Wert. Wird wie
   * `subtitle` immer mitgelesen (auch wenn die Spalte selbst nicht sichtbar ist).
   */
  flag?: ListColumnFlag;
  /** Immer sichtbar (erste Spalte, Nummer bzw. Name); lässt sich nicht ausblenden. */
  alwaysVisible?: boolean;
  /** Ausrichtung; Standard: Zahlen rechts, sonst links. */
  align?: 'left' | 'right';
  /**
   * Zeile der Karte am Handy (Karten-Liste, EXPERIENCE §10.2, Story 1.23): 1 = Zeile 1 (wichtigstes Feld), 2, 3.
   * Ohne Angabe erscheint die Spalte nicht auf der Karte, sobald irgendeine andere Spalte eine Priorität hat. Auswahl der
   * Felder: `mobileCardLayout` (`card.ts`).
   */
  mobilePriority?: MobilePriority;
  /** Zahl immer mit Vorzeichen anzeigen (Story 5.38, Bewegungen: „+20 Stück“, „−2 Stück“; 0 ohne). */
  signed?: boolean;
  /** Massenbearbeitung (Story 1.21). */
  edit?: ListColumnEdit;
  /**
   * Nur Typ `money` (Pflicht): Feld derselben View mit dem Währungscode (ISO 4217). Das Betragsfeld der View ist der
   * Dezimalbetrag in der Währung (z. B. `numeric` 38.9000 EUR); `reporting` rechnet ihn exakt in die kleinste Einheit
   * der Währung um (EUR 2, JPY 0, KWD 3 Nachkommastellen, `platform/currency`).
   */
  currencyField?: string;
  /**
   * Nur Typ `status`: Zwischenstand, solange der Besitzer der Spalte ein Ereignis eines anderen Moduls noch nicht
   * verarbeitet hat (AD-5, z. B. Shopware-Übertragung nach einer Artikeländerung). Ist die Version `versionField` der
   * eigenen View (ohne Zeile: `missingVersion`) kleiner als die Version `currentField` in der View von
   * `currentModule`, zeigen und filtern Liste und Sortierung `value` statt des eigenen Werts – dieselbe Regel wie die
   * Detailansicht des Besitzers. Jede View liest dabei nur das eigene Schema (AD-1); verglichen wird in `reporting`.
   */
  staleWhen?: ListColumnStaleWhen;
  /**
   * Kennzahl-Kopf (FR-95, AD-35, Story 1.32): erlaubte Aggregate dieser Spalte. `sum | avg | min | max` nur für
   * Zahlen und Beträge, `count_true` nur für Ja/Nein; `ratio` und `count_distinct` als Objekt. Durchschnitte und
   * Quoten immer Σ Zähler ÷ Σ Nenner über die gefilterten Zeilen (nie Durchschnitt der Zeilenwerte); Beträge je
   * Währung. Nur Spalten mit Index oder Projektionsspalten (AD-35).
   */
  aggregate?: readonly ListColumnAggregate[];
  /**
   * Kennzahl-Kachel (Ticket 1.294, Entwurf N, Sichtung HS53): „Anzahl mit Merkmal“ (`count_true`) dieser Ja/Nein-Spalte
   * ist ein echter Anteil an den Zeilen der Liste (z. B. aktive Kunden, Artikel unter Mindestbestand) – die Kachel zeigt
   * dann die Leiste „X von N“. Ohne Markierung nur die Zahl (z. B. Neukunden diesen Monat, Kunden gesamt).
   */
  shareOfRows?: boolean;
  /**
   * Kennzahl-Kachel (Ticket 1.294, Clean/EK6, Sichtung HS53): Satz unter „–“, wenn die Summe (`sum`) dieser Spalte leer
   * ist – dann fehlt der Wert in allen Zeilen der Auswahl. `{n}` = Zeilenzahl der Liste zum selben Filter (z. B.
   * „Ø-EK fehlt bei {n} Artikeln“). Ohne Angabe der allgemeine Satz der Oberfläche.
   */
  emptySumNote?: { one: string; many: string };
  /**
   * Verlauf der Kennzahl vom Server (Ticket 1.297): Reihe je Monat bzw. Tag zum aktuellen Filter, als Mini-Säulen in der
   * Kachel. Ohne Angabe keine Reihe (abwärtskompatibel). Arten: {@link ListColumnSeries}.
   */
  series?: ListColumnSeries;
  /** Satz der Kachel, wenn die Reihe im ganzen Fenster keinen Wert hat (`empty`), z. B. „Kein Umsatz in 12 Monaten“. */
  seriesEmptyNote?: string;
  /**
   * Vergleich mit einer anderen Spalte (Filter `compare_column`, Version 2): nur die hier angemeldeten Paare. Jedes
   * Paar ist eine indizierte Ja/Nein-Spalte `field` derselben View des Besitzers (AD-32 „als indizierte
   * Projektionsspalte“), z. B. verfügbar < Mindestbestand.
   */
  comparisons?: readonly ListColumnComparison[];
  /** Schnellsuche (Story 1.32): nur Text-Spalten; der Besitzer liefert einen Trigramm-Index auf `lower(feld)`. */
  searchable?: boolean;
  /**
   * Frei eingegebener Text (Bemerkung, Notiz – nicht Stammdaten): Die KI-Werkzeuge melden die Werte als Freitext
   * (Story 19.7, `AiReadTool.freeText`); bei „Freitext an KI-Modelle: aus“ sieht ein Agent sie leer. Nur Typ `text`.
   */
  freeText?: boolean;
  /**
   * Geltender Wert mit Standard aus den Einstellungen (Story 1.32), z. B. Zahlungsziel: Ist `unlessField` (Ja/Nein
   * derselben View) nicht wahr, gilt der Standardwert, den der Besitzer zum fachlichen Datum liest (Leser im Register
   * von `reporting`). Liste, Filter, Sortierung und Kennzahlen rechnen mit dem geltenden Wert; die Zeile meldet, welche
   * Zellen den Standard zeigen (`defaulted`), die Zelle zeigt dann `label` dazu, z. B. „14 (Standard)“.
   */
  fallback?: ListColumnFallback;
  /**
   * Ja/Nein „im laufenden Kalendermonat“ (Story 4.23, z. B. „Neukunde diesen Monat“): nur Typ `boolean`. `field` ist
   * ein Datumsfeld (`date`) derselben View; `reporting` rechnet den Wert zum fachlichen Datum der Abfrage (Testuhr):
   * wahr, wenn das Datum im Kalendermonat des fachlichen Datums liegt, sonst (auch ohne Datum) falsch. Kein Tageslauf,
   * kein gespeicherter Wert; keine Schnellsuche, keine vorhandenen Werte, keine Massenbearbeitung, nicht sortierbar.
   */
  inCurrentMonth?: true;
  /**
   * Datierter Wert ohne Grundlage bleibt leer (Nacharbeit 12.12): Ohne Zeile des datierten Beitrags (Story 6.17) zeigt
   * die Spalte keinen Wert statt 0 bzw. „nein“ – für Werte, die ohne Grundlage nichts bedeuten (z. B. Tage seit einem
   * Ereignis, das es nicht gibt, oder „ohne Ø-EK“ ohne Bewertungslauf); 0/„nein“ nur bei echtem 0/„nein“. Mengen,
   * Anzahlen und Ja/Nein ohne diese Angabe bleiben 0 bzw. falsch. Nur Typ `number`/`quantity`/`boolean` eines datierten
   * Beitrags, keine Teil-View.
   */
  emptyWithoutBasis?: true;
  /**
   * Warnung an der Zelle (Story 9.40, z. B. „Gültig bis“ gelb, wenn ein Angebot bald abläuft): `statusField` ist ein Feld
   * derselben View, das die Regel des Besitzers mitbekommt. Die Entscheidung trifft der Leser des Besitzers im Register von
   * `reporting` (`listWarningReaders`, reine Funktion, zum fachlichen Datum der Abfrage); die Zeile meldet den Satz je
   * Spalte in `warnings`. Nur Anzeige – Filter, Sortierung, Export und Kennzahlen bleiben unverändert.
   */
  warning?: ListColumnWarning;
  /**
   * Kennzahl aus einem Tageslauf (Story 1.33, AD-35): Modul, dessen Tageslauf (`defineMetricRefresh`) die Spalte rechnet.
   * Die Liste zeigt dazu „Stand: …“ bzw. bei veraltetem Stand einen Hinweis (`GET /lists/{liste}/metrics-status`).
   */
  refresh?: string;
  /**
   * Summe anderer Zahlenspalten derselben Liste (Story 4.19, z. B. „Bestellungen gesamt“ = Shop-Historie + ERP-Aufträge
   * gesamt): `reporting` rechnet zum Abfragezeitpunkt Σ der Operanden, ein leerer Operand zählt 0; `field` ist nur ein
   * Name. Nur Typ `number`; Operanden vom Typ `number` oder `quantity` (keine Beträge, keine Summen-Spalten). Keine
   * Schnellsuche, keine vorhandenen Werte, keine Massenbearbeitung, kein Standardwert, keine Teil-View.
   */
  sumOf?: readonly string[];
  /**
   * Teil-View mit mehreren Zeilen je Schlüssel (Story 4.19, z. B. Umsatz je Bestellwährung): Die Spalte liest nur die
   * Zeile, deren Feld `field` den Wert `value` hat (`reporting` verbindet `… and jX.<field> = <value>`). Die Spaltenwahl
   * bietet sie nur an, wenn der Wert in der Teil-View zum fachlichen Datum vorkommt; gespeicherte Ansichten bleiben gültig.
   */
  rowValue?: ListColumnRowValue;
  /**
   * Geerbter Wert (Story 5.57, z. B. Zusatzfeld einer Variante ohne eigenen Wert): Ja/Nein-Feld derselben View. Ist es
   * wahr, zeigt die Zelle den Wert des Hauptartikels; die Zeile meldet die Spalte in `inherited`, die Zelle ist grau
   * mit dem Zusatz „geerbt“. Filter und Sortierung rechnen mit dem gezeigten Wert.
   */
  inheritedField?: string;
  /**
   * Lesespiegel einer Gegenstelle mit Leseschalter (Story 4.19, Rudolf 11.10.2026, z. B. Shop-Historie aus Shopware):
   * `reporting` fragt bei jeder Abfrage den Leser des Besitzers (Register, je Spaltenschlüssel) nach der Stellung des
   * Schalters. `live`: Feld `field` (Echtwerte). `shadow` (Probe): Feld `probe.field` derselben View – synthetische, feste
   * Beispielwerte ohne Netz –, die Zelle trägt den Zusatz `probe.label`. `off`: leer, die Spalte trägt den Hinweis
   * `probe.offLabel`. Eine Summen-Spalte (`sumOf`) übernimmt Zusatz und Hinweis ihrer Operanden. Keine Schnellsuche,
   * keine vorhandenen Werte, keine Massenbearbeitung, kein Standardwert, keine Teil-View.
   */
  probe?: ListColumnProbe;
}

/**
 * Verlauf einer Listen-Kennzahl (Ticket 1.297, `ListColumn.series`); Bezeichner nur aus dem Code.
 *
 * - `sum` (nur Aggregat `sum`): Summe `valueField` der Tages-Sicht `report.<view>` des Moduls `module` je Monat bzw. Tag
 *   (`grain`) im Zeitraum `window` der Einstellungen „Kennzahlen“, nur Zeilen, deren `keyField` ein Schlüssel der
 *   gefilterten Liste ist (wie `count_distinct`). `dateField` ist ein fachliches Datum. Σ Reihe = Kachelwert, sonst `partial`.
 *   Fenster: siehe {@link ListSeriesWindow}.
 * - `rows` (Aggregat `count_true` oder `sum`): die eigenen Listenzeilen je Kalendermonat der Datumsspalte `dateColumn`
 *   (Typ `date`, dieselbe Liste) über die letzten `months` Monate.
 * - `level` (Aggregat `sum` oder `count_true`): Stand je Monatsende aus Bewegungen – je Schlüssel der letzte `valueField`
 *   der Sicht `report.<view>` vor dem Ende jedes der letzten `months − 1` Monate, dazu heute; vor der ersten Bewegung 0.
 * - `daily` (Aggregat `sum` oder `count_true`): Tagesstand der ungefilterten Liste, ab dem ersten Lauf vom Tageslauf
 *   `reporting` gespeichert (`reporting.list_metric_day`), die letzten `days` Tage; mit Filter oder Suche keine Reihe.
 * - `ratio` (nur Aggregat `ratio`, z. B. Ø Bestellwert): je Monat Σ `numeratorField` ÷ Σ `denominatorField` derselben
 *   Tages-Sicht wie `sum` (Fenster, Schlüssel wie dort), kaufmännisch auf ganze kleinste Einheiten gerundet. Monate ohne
 *   Nenner (z. B. ohne Auftrag) haben keinen Abschnitt – nie ein erfundener Wert. Nicht summierbar: kein Abgleich mit der
 *   Kachel, nie `partial`.
 */
export type ListColumnSeries =
  | {
      kind: 'sum';
      module: string;
      view: string;
      keyField: string;
      dateField: string;
      valueField: string;
      window: ListSeriesWindow;
      grain: 'month' | 'day';
      slice?: ListSeriesSlice;
    }
  | { kind: 'rows'; dateColumn: string; months: number }
  | { kind: 'level'; module: string; view: string; keyField: string; timeField: string; valueField: string; months: number }
  | { kind: 'daily'; days: number }
  | {
      kind: 'ratio';
      module: string;
      view: string;
      keyField: string;
      dateField: string;
      numeratorField: string;
      denominatorField: string;
      window: ListSeriesWindow;
      grain: 'month';
      slice?: ListSeriesSlice;
    };

/**
 * Ausschnitt einer Reihe `sum`/`ratio` ([RUDOLF] 26.10.2026, Ticket 1.297): Steht in der obersten „und“-Ebene der Filter
 * eine positive Bedingung „enthält eines/alle von“ bzw. „ist eines von“ auf der Listenspalte `column` (z. B. Kanal = eBay),
 * zählen Kachel UND Reihe nur die Zeilen der Reihen-Sicht mit `field` in diesen Werten (nur eBay-Umsatz, Σ Reihe =
 * Kachel). Die Kachel rechnet dann aus derselben Sicht wie die Reihe; die Zeilen der Liste bleiben unverändert.
 */
export interface ListSeriesSlice {
  column: string;
  field: string;
}

/**
 * Zeitraum einer Reihe `sum` bzw. `ratio`: `short | medium | long` aus den Einstellungen „Kennzahlen“ (`metric_period_*`), oder
 * `{ viewArg }` = dasselbe Fenster wie der datierte Beitrag des Moduls (Story 6.17): Anfang = Datumsargument Nr.
 * `viewArg` (ab 0, nach `as_of`) seiner Tabellenfunktion, Ende = `as_of` – so rechnen Kachel und Reihe immer mit demselben
 * Zeitraum, auch wenn die Einstellungen der Verkaufszahlen (`sales_period_*`) von den Kennzahlen abweichen.
 */
export type ListSeriesWindow = 'short' | 'medium' | 'long' | { viewArg: number };

/** Name einer Reihen-Sicht im Schema `report`: `ser_<liste>__<modul>` (Ticket 1.297). */
export const SERIES_VIEW_PATTERN = /^ser_[a-z][a-z0-9]*__[a-z][a-z0-9]*$/;

/** Ein Abschnitt der Reihe: erster Tag (ISO; beim ersten Monat ggf. der Fensteranfang) und Wert als Ganzzahl-Text. */
export interface ListMetricSeriesBucket {
  start: string;
  /** Ganzzahl als Text: Cent (Beträge, in der einen Währung der Kachel), Stück oder Anzahl. */
  value: string;
}

/** Verlauf einer Kachel vom Server (Ticket 1.297): ältester Abschnitt zuerst. */
export interface ListMetricSeries {
  grain: 'month' | 'day';
  buckets: ListMetricSeriesBucket[];
  /** Kein Wert im ganzen Fenster (alle Abschnitte 0). */
  empty: boolean;
  /** Die Reihe deckt die Kachel nicht ganz ab (Σ bzw. letzter Abschnitt ≠ Kachelwert). */
  partial: boolean;
  /** Satz für die Fußzeile, z. B. „Säulen: letzte 12 Monate“ oder „Verlauf ab 27.10.2026“. */
  note?: string;
}

/** Beispielwerte einer Spalte bei Leseschalter „Probe“ (`ListColumn.probe`); Bezeichner nur aus dem Code. */
export interface ListColumnProbe {
  /** Feld derselben View mit dem Beispielwert. */
  field: string;
  /** Nur Beträge: Feld derselben View mit der Währung des Beispielwerts. */
  currencyField?: string;
  /** Zusatz in Zelle und Spaltenkopf bei Probe, z. B. „Probe – Beispieldaten“. */
  label: string;
  /** Hinweis im Spaltenkopf bei „aus“, z. B. „Shopware – lesen: aus“. */
  offLabel: string;
}

/** Feste Zeile einer Teil-View (`ListColumn.rowValue`); Bezeichner und Wert nur aus dem Code. */
export interface ListColumnRowValue {
  field: string;
  value: string;
}

/** Warnung an der Zelle (`ListColumn.warning`). */
export interface ListColumnWarning {
  /** Feld derselben View (Bezeichner nur aus dem Code), das die Regel zusammen mit dem Zellwert bekommt. */
  statusField: string;
}

/** Kurzwort vor dem Wert einer Spalte (`ListColumn.flag`). */
export interface ListColumnFlag {
  /** Schlüssel der Ja/Nein-Spalte derselben Liste. */
  column: string;
  /** Wort, z. B. „Express“. */
  label: string;
}

export interface ListColumnFallback {
  unlessField: string;
  label: string;
}

/** Vergleichsoperatoren für `compare_column` (Spalte gegen Spalte). */
export const listCompareOperators = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte'] as const;
export type ListCompareOperator = (typeof listCompareOperators)[number];

/** Angemeldetes Vergleichspaar: `<diese Spalte> <op> <column>` ist wahr, wenn `field` (Ja/Nein, indiziert) wahr ist. */
export interface ListColumnComparison {
  op: ListCompareOperator;
  /** Schlüssel der anderen Spalte derselben Liste. */
  column: string;
  /** Ja/Nein-Feld derselben View (Bezeichner nur aus dem Code). */
  field: string;
}

/** Einfache Aggregate einer Spalte. */
export const listSimpleAggregates = ['sum', 'avg', 'min', 'max', 'count_true'] as const;
export type ListSimpleAggregate = (typeof listSimpleAggregates)[number];

/** Alle Arten einer Kennzahl-Kachel; `count` = Zahl der Zeilen (ohne Spalte). */
export const listAggregateKinds = ['count', ...listSimpleAggregates, 'ratio', 'count_distinct'] as const;
export type ListAggregateKind = (typeof listAggregateKinds)[number];

/**
 * Quote bzw. gewichteter Durchschnitt: Σ `numerator` ÷ Σ `denominator` über die gefilterten Zeilen. Beide sind
 * Spalten derselben Liste (Projektionsspalten); ist der Zähler ein Betrag, gilt das Ergebnis je Währung. Anteil
 * (Story 4.23): Sind Zähler und Nenner Beträge derselben View mit demselben Währungsfeld, ist das Ergebnis ein Anteil
 * in Prozent (100 × Σ ÷ Σ, nur wenn alle beteiligten Zeilen dieselbe Währung haben, sonst leer).
 */
export interface ListRatioAggregate {
  kind: 'ratio';
  numerator: string;
  denominator: string;
}

/**
 * Zahl unterschiedlicher IDs über eine Beziehungs-View `report.<view>` des Moduls `module` (AD-35): gezählt werden
 * unterschiedliche `distinctField` der Zeilen, deren `keyField` ein Schlüssel der gefilterten Liste ist – nie die
 * Summe der Zeilenwerte (ein Auftrag mit zwei gefilterten Artikeln zählt einmal).
 */
export interface ListCountDistinctAggregate {
  kind: 'count_distinct';
  relation: ListAggregateRelation;
}

export interface ListAggregateRelation {
  module: string;
  /** View im Schema `report`, Name `rel_<a>_<b>__<modul>`. */
  view: string;
  /** Spalte der Beziehungs-View mit dem Schlüssel der Listenzeile. */
  keyField: string;
  /** Spalte mit der gezählten ID (z. B. Auftrag, Kunde). */
  distinctField: string;
  /**
   * Nur Zeilen im Zeitraum (Story 6.19, z. B. „Aufträge 12 Monate“): `dateField` (fachliches Datum der Beziehungs-View)
   * zwischen Anfang und Ende des Zeitraums `window` aus den Einstellungen der Gruppe „Kennzahlen“ (`periodWindow` zum
   * fachlichen Datum der Abfrage). Ohne Angabe: alle Zeilen.
   */
  period?: { dateField: string; window: 'short' | 'medium' | 'long' };
}

export type ListColumnAggregate = ListSimpleAggregate | ListRatioAggregate | ListCountDistinctAggregate;

/** Art eines angemeldeten Aggregats. */
export function aggregateKind(aggregate: ListColumnAggregate): Exclude<ListAggregateKind, 'count'> {
  return typeof aggregate === 'string' ? aggregate : aggregate.kind;
}

/**
 * Eine Kennzahl: Zahl der Zeilen oder ein angemeldetes Aggregat einer Spalte. `label` (Story 4.23): eigene Beschriftung
 * der Kachel bzw. des Teilwerts (z. B. „Kunden gesamt“, „Shopware“); ohne Angabe die Beschriftung aus `metricLabel`.
 */
export type ListSingleMetric = ({ aggregate: 'count' } | { column: string; aggregate: Exclude<ListAggregateKind, 'count'> }) & { label?: string };

/**
 * Kachel-Form „Aufteilung“ (Story 4.23, Change 06.10.2026 Ideen F11): eine Kachel mit 2–3 Teilwerten nebeneinander,
 * jeder ein vorhandenes Aggregat (keine neue Rechenart), z. B. „Umsatz nach Kanal · Shopware 60 % · eBay 15 % · ERP 25 %“.
 * Zählt als eine Kachel. Sind alle Teilwerte Anteile in Prozent, rundet `reporting` sie auf ganze Prozent (Rest im
 * größten Anteil, `splitPercents`).
 */
export interface ListSplitMetric {
  /** Beschriftung der Kachel, z. B. „Umsatz nach Kanal“. */
  split: string;
  parts: ListSingleMetric[];
}

/** Kennzahl-Kachel einer Ansicht: eine Kennzahl oder eine Aufteilung. */
export type ListMetric = ListSingleMetric | ListSplitMetric;

/** Ist die Kachel eine Aufteilung? */
export function isSplitMetric(metric: ListMetric): metric is ListSplitMetric {
  return 'split' in metric;
}

/** Versionsvergleich für einen Zwischenstand (`ListColumn.staleWhen`). Bezeichner nur aus dem Code. */
export interface ListColumnStaleWhen {
  versionField: string;
  currentModule: string;
  currentField: string;
  missingVersion: number;
  value: string;
}

/** Wert einer Zelle vom Typ `money`: Betrag exakt in der kleinsten Einheit als Ziffernfolge, mit Währungscode. */
export interface ListMoneyValue {
  amountMinor: string;
  currency: string;
}

export type SortDirection = 'asc' | 'desc';

export interface ListSort {
  column: string;
  direction: SortDirection;
}

/** Eine Liste (Hauptbesitzer = Besitzer des Zeilenobjekts, AD-2). */
export interface ListDefinition {
  /** Fester Schlüssel, z. B. `customers`. */
  key: string;
  /** Deutscher Name, z. B. „Kunden“. */
  label: string;
  /** Wort für eine bzw. mehrere Zeilen, z. B. „Kunde“ / „Kunden“. */
  rowNoun: { one: string; many: string };
  /** Hauptbesitzer: seine View `report.list_<liste>__<besitzer>` ist die Hauptview. */
  ownerModule: string;
  /** Schlüsselspalte jeder View dieser Liste. */
  rowKey: 'id';
  /** Sortierung ohne Wahl (und zweiter Schlüssel des Keyset-Pagings ist immer `rowKey`). */
  defaultSort: ListSort;
  /** Erste, immer sichtbare Spalte (Nummer bzw. Name). */
  titleColumn: string;
  /** Satz, wenn die Liste ganz leer ist, z. B. „Noch keine Kunden.“ */
  emptyText: string;
  /** Story 9.41 (opt-in): eigener Satz, wenn Filter oder Suche nichts finden; Standard „Keine <Nomen> mit diesen Filtern.“ */
  noMatchText?: string;
  /**
   * Spalte, die jede Zeile immer mitliefert, um den zugehörigen Datensatz zu öffnen, wenn er nicht der Zeilenschlüssel
   * ist (Story 7.16: Rückstandsposition öffnet ihren Auftrag über `orders.order_id`). Wird mitgelesen wie ein Untertitel,
   * aber nur exportiert, wenn sie selbst gewählt ist. Ohne Angabe: Öffnen über den Zeilenschlüssel.
   */
  openColumn?: string;
  /**
   * `process` = Liste von Vorgängen (Aufträge, Belege, Zahlungen …): keine Spalte mit `edit`, kein „Bearbeiten“ –
   * nur feste Sammel-Aktionen (AD-32). Ohne Angabe: Stammdaten-Liste.
   */
  kind?: 'master' | 'process';
  /**
   * Start-Kacheln der Liste in fester Reihenfolge (Story 4.23); dürfen Spalten anderer Beiträge nennen (geprüft, wenn
   * alle Beiträge da sind). Ohne Angabe: Start-Kacheln der Beiträge in Register-Reihenfolge.
   */
  defaultMetrics?: readonly ListMetric[];
  /**
   * Suchdokument (Bug 1.52, AD-32): Die Hauptview des Besitzers liefert die Spalte `search_folded` – die Such-Faltung
   * (`foldSearchSql`) seiner Suchfelder in Register-Reihenfolge, getrennt durch {@link SEARCH_DOCUMENT_SEPARATOR}, als
   * gespeicherte Spalte mit Trigramm-Index. Die Schnellsuche prüft je Begriff dann nur diese Spalte statt jedes Feld
   * einzeln zu falten (gleiche Treffer). Nur, wenn alle Suchfelder dem Besitzer gehören.
   */
  searchDocument?: true;
  /**
   * Schnellsuche über das Verbindungsfeld (Story 11.41): Die Hauptview ist teuer zu lesen (Anti-Join auf das jüngste Label)
   * und jeder Begriff soll sie nicht noch einmal ganz lesen. Treffer eines verbundenen Moduls werden dann als
   * `m.<joinOn> in (select s.id from <view> s where …)` geprüft statt über `select k.id from <hauptview> k join …`; das
   * Ergebnis ist gleich. Nur für Listen, deren Hauptview je Zeile genau einen Verbindungswert hat.
   *
   * `'row'` (epic-datev-export 16.10, Anzahlungen ohne Rechnung): für Listen, die die Seite immer auf wenige Zeilen
   * eingrenzt (ein Lauf), deren Hauptview aber sehr viele Zeilen hat. Felder des Besitzers werden in der Zeile selbst
   * geprüft, verbundene Module als `exists (… where s.id = m.<joinOn> and …)` – Postgres wählt dann zwischen einer Probe je
   * Zeile und gesammelten Treffern. Gleiches Ergebnis wie `true`.
   */
  searchByJoinKey?: true | 'row';
  /**
   * Stichtag-Liste (Story 5.44, z. B. „Lagerwert zum Stichtag“): Jede Anfrage (Zeilen, Weiterblättern, Kacheln, Gruppen,
   * Schlüssel, Spaltenoptionen, Export) nimmt ein eigenes Feld `asOf` (ISO-Datum, nicht nach dem fachlichen Heute; ohne
   * Angabe heute). Teil-Views des Hauptbesitzers (`ListColumn.view`) sind dann datierte Tabellenfunktionen
   * `report.list_<liste>__<modul>__<view>(as_of date)` mit diesem Stichtag. Der Stichtag gehört nicht zur gespeicherten
   * Ansicht. `label` = Beschriftung des Felds (z. B. „Stichtag“). Andere Listen lehnen `asOf` ab (422).
   */
  asOf?: { label: string };
  /**
   * Nur für Admins (Story 18.27, „Zahlungseingänge“): Alle Listen-Endpunkte (Zeilen, Kacheln, Gruppen, Ansichten,
   * Export …) antworten Nicht-Admins mit 403; Datenfragen und KI bekommen die Liste nicht angeboten.
   */
  adminOnly?: true;
}

/** Trenner der Felder im Suchdokument (U+001F); ein Suchbegriff damit sucht weiter je Feld. */
export const SEARCH_DOCUMENT_SEPARATOR = '\u001f';

/** Beitrag eines Moduls zu einer Liste; der Hauptbesitzer liefert zusätzlich die Definition. */
export interface ListContribution {
  list: string;
  module: string;
  definition?: ListDefinition;
  columns: readonly ListColumn[];
  /**
   * Nur für Beiträge anderer Module (Story 5.38): Feld der Hauptview, über das die eigene View (Schlüssel `id`)
   * verbunden wird – z. B. `article_id`, wenn die Zeile eine Bestandsbewegung ist und catalog Artikelnummer und Name
   * beiträgt. Ohne Angabe: Schlüssel der Hauptview (`id`). Die Hauptview muss das Feld liefern (Index des Besitzers).
   */
  joinOn?: string;
  /**
   * Datierter Beitrag (Story 6.17, Kennzahlen mit Zeitraum bis zum Tageslauf 6.19): Statt einer View veröffentlicht das
   * Modul eine Tabellenfunktion `report.list_<liste>__<modul>(as_of date, …)` mit Schlüssel `id`. `reporting` ruft sie
   * mit dem fachlichen Datum der Abfrage und den Datumsargumenten auf, die der Leser des Besitzers im Register von
   * `reporting` zum fachlichen Datum liefert (z. B. Anfänge der Zeiträume). Nur für Beiträge anderer Module (nie die
   * Hauptview); keine Suchfelder, keine „vorhandenen Werte“, keine Massenbearbeitung. Zeilen ohne Treffer in der
   * Funktion (z. B. nie verkaufter Artikel): Spalten vom Typ `quantity` und `number` gelten als 0 (Anzeige, Filter,
   * Sortierung, Kennzahlen), Ja/Nein als falsch, außer mit `emptyWithoutBasis`; Beträge und Daten bleiben leer.
   */
  dated?: boolean;
  /**
   * Start-Kacheln des Kennzahl-Kopfs aus diesem Beitrag (Story 1.32), nur über eigene Spalten (und `count` beim
   * Hauptbesitzer). Die Liste reiht sie in Register-Reihenfolge der Beiträge auf (höchstens 6) – außer die Definition
   * nennt die Start-Kacheln selbst (`ListDefinition.defaultMetrics`).
   */
  defaultMetrics?: readonly ListMetric[];
  /**
   * Beziehungsfilter (Story 1.33, AD-35): Filter über eine Beziehungs-View dieses Moduls, z. B. „Kunden, die … gekauft
   * haben / nie gekauft haben“. `reporting` übersetzt sie in `EXISTS`/`NOT EXISTS` über den Index (Schlüssel, Datum).
   */
  relations?: readonly ListRelationFilter[];
}

/**
 * Spaltenfamilie (Story 5.57, AD-32 „Dynamische Spaltengruppen“): Spalten, die der Besitzer zur Laufzeit liefert,
 * z. B. je Zusatzfeld des Artikels eine Spalte. `reporting` fragt `signature` (billig) zum fachlichen Zeitpunkt und baut
 * bei geänderter Signatur das Register mit `columns` neu. Die Spalten lesen die Teil-View `view` des Moduls
 * (`report.list_<liste>__<modul>__<view>`); sie bilden einen eigenen Beitrag ohne Definition. `Q` ist der
 * Datenbankzugang des Aufrufers (die Plattform-Verträge bleiben frei von Datenbank-Typen).
 */
export interface ListColumnFamily<Q = unknown> {
  list: string;
  module: string;
  view: string;
  signature(db: Q, at: Date): Promise<string>;
  columns(db: Q, at: Date): Promise<readonly ListColumn[]>;
}

/**
 * Beziehungsfilter (AD-35): Der Besitzer der Beziehung veröffentlicht `report.<view>` (Name `rel_<a>_<b>__<modul>`) mit
 * dem Schlüssel der Listenzeile (`keyField`), dem fachlichen Datum je Kauftag (`dateField`, eine Zeile je Paar und Tag)
 * und optional einer Menge (`quantityField`). Ziele (`targets`) schränken auf Werte eines Felds der View ein (z. B.
 * Artikel, Kategorie, Hersteller). Bezeichner nur aus dem Code; der Besitzer liefert einen Index (Schlüssel, Datum).
 */
export interface ListRelationFilter {
  /** Fester Schlüssel `<modul>.<name>`, z. B. `orders.bought_article`. */
  key: string;
  /** Beschriftung der Filter-Gruppe, z. B. „Gekauft“. */
  label: string;
  /** Satz für „hat …“ bzw. „hat nie …“, z. B. „hat gekauft“ / „hat nie gekauft“. */
  hasLabel: string;
  hasNotLabel: string;
  module: string;
  /** View im Schema `report`. */
  view: string;
  keyField: string;
  dateField: string;
  quantityField?: string;
  targets: readonly ListRelationTarget[];
}

/** Ziel eines Beziehungsfilters: Feld der Beziehungs-View, dessen Werte die Bedingung nennt. */
export interface ListRelationTarget {
  /** Schlüssel innerhalb der Beziehung, z. B. `article`. */
  key: string;
  /** Beschriftung, z. B. „Artikel“. */
  label: string;
  field: string;
  /** Liste, aus der die Werte stammen (Auswahl in der Oberfläche), z. B. `articles`. */
  list?: string;
  /**
   * Ziel über eine Spalte der Liste `list` (Story 6.19, z. B. Hersteller): Die Werte der Bedingung sind Werte der Spalte
   * `column`; `field` der Beziehungs-View wird mit der Spalte `match` derselben View verglichen (z. B. Artikelnummer).
   * Beide Spalten gehören der Hauptview von `list` (Bezeichner aus dem Register, geprüft); so liest kein Modul ein fremdes
   * Schema – `reporting` verbindet die Views.
   */
  via?: { column: string; match: string };
}

/** Name der Beziehungs-View: `rel_<a>_<b>__<modul>`. */
export const RELATION_VIEW_PATTERN = /^rel_[a-z][a-z0-9]*_[a-z][a-z0-9]*__[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

/**
 * Gruppieren (Story 1.33, FR-95): nur nach einer filterbaren Auswahl-Spalte – Auswahl, Status, Ja/Nein oder Text mit
 * vorhandenen Werten (z. B. Land). Rein.
 */
export function isGroupableColumn(column: Pick<ListColumn, 'type' | 'filterable' | 'distinctOptions'>): boolean {
  if (!column.filterable) return false;
  return column.type === 'enum' || column.type === 'status' || column.type === 'boolean' || (column.type === 'text' && column.distinctOptions === true);
}

/** Zahlenartige Typen: rechtsbündig, Vergleichs-Operatoren. */
export const numericListColumnTypes: readonly ListColumnType[] = ['number', 'money', 'quantity', 'percent'];

export function isNumericColumnType(type: ListColumnType): boolean {
  return numericListColumnTypes.includes(type);
}

/** Ausrichtung in der Zelle: Zahlen rechts (DESIGN „Werte in Zellen“). */
export function columnAlign(column: Pick<ListColumn, 'type' | 'align'>): 'left' | 'right' {
  return column.align ?? (isNumericColumnType(column.type) ? 'right' : 'left');
}

/** Satz, wenn Filter nichts finden, z. B. „Keine Kunden mit diesen Filtern.“ (EXPERIENCE §5.3). */
export function noMatchText(definition: Pick<ListDefinition, 'rowNoun' | 'noMatchText'>): string {
  return definition.noMatchText ?? `Keine ${definition.rowNoun.many} mit diesen Filtern.`;
}

/** Name der Report-View eines Beitrags (ohne Schema): `list_<liste>__<modul>`, Teil-View `list_<liste>__<modul>__<view>`. */
export function listViewName(list: string, module: string, view?: string): string {
  const name = `list_${list}__${module.replace(/[^a-z0-9_]/g, '_')}`;
  return view ? `${name}__${view.replace(/[^a-z0-9_]/g, '_')}` : name;
}


/** Spalte des Suchdokuments in der Hauptview des Besitzers. */
export const SEARCH_DOCUMENT_FIELD = 'search_folded';
